import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, Loading } from "@/components/ui-kit";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { subjectTheme } from "@/lib/subjects";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid, Legend } from "recharts";

const COLORS = ["#7C3AED", "#059669", "#D97706", "#2563EB", "#DB2777"];

export default function Progress() {
  const [progress, setProgress] = useState(null);
  const [sel, setSel] = useState("all");

  useEffect(() => { api.get("/student/progress").then((r) => { setProgress(r.data); if (r.data[0]) setSel(`${r.data[0].program}::${r.data[0].section}`); }); }, []);

  const section = progress ? (progress.find((s) => `${s.program}::${s.section}` === sel) || progress[0]) : null;

  // merge skill timelines into one chart data by week
  const chartData = useMemo(() => {
    if (!section) return [];
    const byWeek = {};
    section.skills.forEach((sk) => sk.timeline.forEach((t) => {
      byWeek[t.week] = byWeek[t.week] || { week: t.week };
      byWeek[t.week][sk.topic] = t.accuracy;
    }));
    return Object.values(byWeek);
  }, [section]);

  if (!progress) return <Loading />;
  const theme = section ? subjectTheme(section.section, section.section_name) : null;
  if (progress.length === 0) return (<div className="animate-fade-up"><PageHeader title="Progress" /><div className="ember-card py-16 text-center text-sm text-[#948EAB]">Complete a homework to build your progress.</div></div>);

  return (
    <div className="animate-fade-up">
      <PageHeader title="Progress Tracker" subtitle="Skill-by-skill mastery, tracked across every homework."
        right={
          <Select value={sel} onValueChange={setSel}>
            <SelectTrigger data-testid="progress-section-filter" className="h-10 w-64 rounded-xl"><SelectValue /></SelectTrigger>
            <SelectContent>{progress.map((s) => <SelectItem key={`${s.program}::${s.section}`} value={`${s.program}::${s.section}`}>{s.program_name.split(" ")[0]} · {s.section_name}</SelectItem>)}</SelectContent>
          </Select>
        } />

      <div className="ember-card mb-6 p-6">
        <div className="flex items-end justify-between">
          <div><h2 className="font-heading text-xl font-extrabold text-[#1E1B2E]">{section.section_name}</h2><p className="text-sm text-[#948EAB]">{section.program_name}</p></div>
          <div className="text-right"><div className="font-heading text-4xl font-extrabold" style={{ color: theme.hex }} data-testid="progress-overall">{section.overall}%</div><div className="text-xs font-semibold text-[#948EAB]">Overall accuracy</div></div>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="ember-card p-6">
          <h3 className="mb-4 font-heading text-lg font-bold text-[#1E1B2E]">Skill accuracy</h3>
          <div className="space-y-3">
            {section.skills.map((sk) => {
              const color = sk.accuracy >= 80 ? "#059669" : sk.accuracy >= 65 ? "#7C3AED" : "#E11D48";
              return (
                <div key={sk.topic}>
                  <div className="mb-1 flex justify-between text-sm"><span className="font-semibold text-[#1E1B2E]">{sk.topic}</span><span className="font-bold" style={{ color }}>{sk.accuracy}%</span></div>
                  <div className="h-2.5 w-full overflow-hidden rounded-full" style={{ backgroundColor: theme.softHex }}><div className="h-full rounded-full" data-testid={`skill-bar-${sk.topic}`} style={{ width: `${sk.accuracy}%`, backgroundColor: theme.hex }} /></div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="ember-card p-6">
          <h3 className="mb-4 font-heading text-lg font-bold text-[#1E1B2E]">Overall over time</h3>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={section.timeline}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F1EEFA" />
              <XAxis dataKey="week" stroke="#948EAB" fontSize={12} />
              <YAxis domain={[0, 100]} stroke="#948EAB" fontSize={12} />
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E9E5F5" }} />
              <Line type="monotone" dataKey="percentage" name="Overall" stroke={theme.hex} strokeWidth={3} dot={{ r: 4, fill: theme.hex }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {chartData.length > 1 && (
        <div className="ember-card mt-6 p-6">
          <h3 className="mb-4 font-heading text-lg font-bold text-[#1E1B2E]">Skills over time</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F1EEFA" />
              <XAxis dataKey="week" stroke="#948EAB" fontSize={12} />
              <YAxis domain={[0, 100]} stroke="#948EAB" fontSize={12} />
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E9E5F5" }} />
              <Legend />
              {section.skills.map((sk, i) => <Line key={sk.topic} type="monotone" dataKey={sk.topic} stroke={COLORS[i % COLORS.length]} strokeWidth={2.5} dot={{ r: 3 }} />)}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
